/**
 * `서 / 혼 / 각인` 플레이어-facing 노출 문자열 시드.
 *
 * 전투 수치나 엔진 로직이 아니라, 카드/툴팁/보상/추천 조합에 바로 붙일 수 있는
 * 한 줄 문구와 연출 어휘를 모아 둔다.
 */

export interface SeoPresentationEntry {
    title: string;
    tagline: string;
    masteryTitle: string;
    masteryRewardName: string;
    cardLines: string[];
}

export interface HonPresentationEntry {
    title: string;
    tagline: string;
    masteryTitle: string;
    masteryRewardName: string;
    cardLines: string[];
}

export interface EngravePresentationEntry {
    title: string;
    shortLabel: string;
    tagline: string;
    shorthand: string;
    weaponSetNames: string[];
}

export interface SoulSeoHonLoadout {
    soulId: string;
    seo: string[];
    hon: string[];
    engraves: string[];
    summary: string;
}

export const SEO_PRESENTATION: Record<string, SeoPresentationEntry> = {
    resolve: {
        title: "결단의 서",
        tagline: "낮아진 숨을 망설임 없이 끊어 내는 글.",
        masteryTitle: "단두",
        masteryRewardName: "단두의 검",
        cardLines: [
            "마지막 한 줄을 그을 자만이 이 글을 끝까지 읽는다.",
            "체력이 꺾인 적 앞에서 가장 날카롭게 빛나는 마무리의 글이다.",
        ],
    },
    flash: {
        title: "섬광의 서",
        tagline: "비껴 맞고 먼저 꽂는 섬광의 몸글.",
        masteryTitle: "잔광",
        masteryRewardName: "잔광의 쌍날",
        cardLines: [
            "회피와 명중, 공속이 한 호흡으로 이어지는 글이다.",
            "보이지 않던 반 박자가 그대로 선공이 된다.",
        ],
    },
    guard: {
        title: "수호의 서",
        tagline: "끌어세우고 버텨 갚는 성벽의 글.",
        masteryTitle: "불괴",
        masteryRewardName: "불괴의 성갑",
        cardLines: [
            "적을 자기 앞에 세우고 끝내 눌러 버티는 글이다.",
            "방어를 딜로 바꾸는 순간, 성벽도 이빨을 드러낸다.",
        ],
    },
    pursuit: {
        title: "추격의 서",
        tagline: "붙고 빠지며 거리를 지배하는 추적의 글.",
        masteryTitle: "끝쫓음",
        masteryRewardName: "추살의 장창",
        cardLines: [
            "원거리의 카이팅과 근거리의 아웃복싱을 함께 품는다.",
            "들어갈 때와 빠질 때를 모두 자기 거리로 바꾸는 글이다.",
        ],
    },
    undying: {
        title: "불멸의 서",
        tagline: "죽음 문턱에서 다시 일어나는 뜻의 글.",
        masteryTitle: "소생",
        masteryRewardName: "소생의 심장고리",
        cardLines: [
            "불사는 끝을 미루고, 소생은 끝을 되돌린다.",
            "이 글은 버티는 데서 끝나지 않고 다시 일어서는 데서 완성된다.",
        ],
    },
    wager: {
        title: "승부의 서",
        tagline: "치명 한 번에 모든 값을 거는 도박의 글.",
        masteryTitle: "결판",
        masteryRewardName: "승부의 도끼",
        cardLines: [
            "치명타 한 번으로 판을 갈라 버리는 고위험의 글이다.",
            "많이 치는 길이 아니라, 한 번을 깊게 박는 길이다.",
        ],
    },
};

export const HON_PRESENTATION: Record<string, HonPresentationEntry> = {
    flame: {
        title: "화염의 혼",
        tagline: "타오르며 번지고 끝내 터지는 혼.",
        masteryTitle: "천염",
        masteryRewardName: "천염의 지팡이",
        cardLines: [
            "숨보다 먼저 불꽃이 대답한다.",
            "한 점의 불씨가 계절 전체를 바꾼다.",
        ],
    },
    frost: {
        title: "서리의 혼",
        tagline: "느리게 굳히고 마침내 멈추게 하는 혼.",
        masteryTitle: "빙결",
        masteryRewardName: "빙결의 설장",
        cardLines: [
            "차갑게 늦추고, 끝내 멈추게 한다.",
            "움직임이 꺼진 자리에는 늘 서리가 남는다.",
        ],
    },
    dark: {
        title: "어둠의 혼",
        tagline: "빛이 닿지 않는 틈에서 힘을 거두는 혼.",
        masteryTitle: "심연",
        masteryRewardName: "심연의 야장",
        cardLines: [
            "보이지 않는 빚이 마침내 값을 받는다.",
            "어둠은 숨기는 힘이 아니라, 늦게 도착하는 힘이다.",
        ],
    },
    wind: {
        title: "바람의 혼",
        tagline: "몸보다 먼저 흐름을 움직이게 하는 혼.",
        masteryTitle: "창공",
        masteryRewardName: "창공의 풍장",
        cardLines: [
            "닿기 전부터 이미 판이 기운다.",
            "가장 빠른 것은 발이 아니라 흐름이다.",
        ],
    },
    starlight: {
        title: "별빛의 혼",
        tagline: "어둠을 걷고 남은 자를 붙드는 혼.",
        masteryTitle: "영광",
        masteryRewardName: "영광의 성흔",
        cardLines: [
            "꺼지지 않은 빛이 끝내 응답한다.",
            "정화와 축복은 마지막까지 남은 이의 몫이다.",
        ],
    },
};

export const ENGRAVE_PRESENTATION: Record<string, EngravePresentationEntry> = {
    sunset: {
        title: "노을",
        shortLabel: "노을 각인",
        tagline: "터짐과 마무리를 부르는 저녁빛의 각인.",
        shorthand: "노각",
        weaponSetNames: ["저녁칼", "노을도끼"],
    },
    wing: {
        title: "나래",
        shortLabel: "나래 각인",
        tagline: "몸을 가볍게 띄우는 날갯짓의 각인.",
        shorthand: "나각",
        weaponSetNames: ["나래칼", "나래활"],
    },
    rapid: {
        title: "여울",
        shortLabel: "여울 각인",
        tagline: "적의 걸음을 꺾는 빠른 흐름의 각인.",
        shorthand: "여각",
        weaponSetNames: ["여울창", "여울활"],
    },
    dusk: {
        title: "어스름",
        shortLabel: "어스름 각인",
        tagline: "시야와 판단을 흐리게 하는 저녁 그늘의 각인.",
        shorthand: "어각",
        weaponSetNames: ["그늘칼", "어스름총"],
    },
    echo: {
        title: "메아리",
        shortLabel: "메아리 각인",
        tagline: "한 번의 타격을 되울리는 반향의 각인.",
        shorthand: "메각",
        weaponSetNames: ["메아리활", "메아리지팡이"],
    },
    shimmer: {
        title: "윤슬",
        shortLabel: "윤슬 각인",
        tagline: "치명의 반짝임을 머금는 물빛 각인.",
        shorthand: "윤각",
        weaponSetNames: ["윤슬지팡이", "윤슬검"],
    },
    galaxy: {
        title: "미리내",
        shortLabel: "미리내 각인",
        tagline: "깊은 기운을 되돌려 주는 은하수의 각인.",
        shorthand: "미각",
        weaponSetNames: ["미리내봉", "미리내활"],
    },
    star: {
        title: "별빛",
        shortLabel: "별빛 각인",
        tagline: "마무리 뒤에 남는 맑은 빛의 각인.",
        shorthand: "별각",
        weaponSetNames: ["별빛검", "별빛봉"],
    },
    moon: {
        title: "달빛",
        shortLabel: "달빛 각인",
        tagline: "위태로운 순간일수록 깊어지는 빛의 각인.",
        shorthand: "달각",
        weaponSetNames: ["달빛칼", "달빛봉"],
    },
    dawn: {
        title: "새벽",
        shortLabel: "새벽 각인",
        tagline: "끝낸 뒤 다시 일어설 힘을 남기는 각인.",
        shorthand: "새각",
        weaponSetNames: ["새벽창", "새벽검"],
    },
    sky: {
        title: "하늘",
        shortLabel: "하늘 각인",
        tagline: "아무 데나 스며드는 가장 담백한 각인.",
        shorthand: "하각",
        weaponSetNames: ["하늘칼", "하늘활", "하늘봉"],
    },
};

export const SOUL_RECOMMENDED_LOADOUTS: SoulSeoHonLoadout[] = [
    {
        soulId: "sword-siasin",
        seo: ["결단의 서", "섬광의 서"],
        hon: ["화염의 혼", "어둠의 혼"],
        engraves: ["노을", "어스름", "여울"],
        summary: "시아신에게 결단은 늦기 전에 숨을 끊어야 했던 밤의 되새김이고, 섬광은 눈보다 몸이 먼저 움직이게 된 뒤의 칼입니다. 노을과 어스름은 그날 끝내 놓지 못한 얼굴의 잔광을, 여울은 망설임이 다시 차오르기 전에 미끄러지듯 파고드는 걸음을 닮았습니다.",
    },
    {
        soulId: "druid-velm",
        seo: ["추격의 서", "불멸의 서"],
        hon: ["바람의 혼", "별빛의 혼"],
        engraves: ["나래", "여울", "새벽"],
        summary: "벨름은 숲과 동행의 감각이 강해 추격과 생환 계열이 잘 어울린다. 바람, 새벽, 여울처럼 살아 있는 흐름의 각인이 자연스럽다.",
    },
    {
        soulId: "machinist-iren",
        seo: ["수호의 서", "섬광의 서"],
        hon: ["화염의 혼", "별빛의 혼"],
        engraves: ["메아리", "윤슬", "별빛"],
        summary: "이렌은 계산된 동작과 공명 계열이 잘 맞는다. 메아리, 윤슬, 별빛은 장치와 정밀함의 이미지를 함께 살린다.",
    },
    {
        soulId: "paladin-ruen",
        seo: ["수호의 서", "불멸의 서"],
        hon: ["별빛의 혼", "서리의 혼"],
        engraves: ["별빛", "새벽", "하늘"],
        summary: "루엔은 수호와 생환의 무게가 중심이다. 빛과 새벽 계열 각인은 무너진 뒤 다시 일어나는 기사 서사와 잘 붙는다.",
    },
    {
        soulId: "courtplay-lisca",
        seo: ["섬광의 서", "결단의 서"],
        hon: ["별빛의 혼", "어둠의 혼"],
        engraves: ["윤슬", "어스름", "달빛"],
        summary: "리스카는 시선을 끌고, 늦게 남는 여운을 다루는 영혼이다. 윤슬과 달빛 같은 반짝임 계열이 특히 잘 맞는다.",
    },
    {
        soulId: "wildhunt-kairo",
        seo: ["추격의 서", "섬광의 서"],
        hon: ["바람의 혼", "어둠의 혼"],
        engraves: ["여울", "나래", "어스름"],
        summary: "카이로는 앞서 보고 앞서 치는 감각이 강하다. 빠른 흐름과 그늘을 주는 각인이 추적자의 손맛을 살린다.",
    },
    {
        soulId: "pilgrim-elda",
        seo: ["수호의 서", "불멸의 서"],
        hon: ["별빛의 혼", "서리의 혼"],
        engraves: ["새벽", "하늘", "미리내"],
        summary: "엘다는 오래 걷고 오래 버티는 쪽에 어울린다. 새벽과 미리내는 순례와 회복의 감각을 함께 준다.",
    },
    {
        soulId: "bladescholar-ionen",
        seo: ["결단의 서", "섬광의 서"],
        hon: ["바람의 혼", "별빛의 혼"],
        engraves: ["윤슬", "메아리", "하늘"],
        summary: "이오넨은 검과 해석을 함께 다루는 혼성형이다. 윤슬과 메아리는 정교함과 재현의 감각을 살린다.",
    },
    {
        soulId: "rune-lancer-serik",
        seo: ["추격의 서", "결단의 서"],
        hon: ["서리의 혼", "별빛의 혼"],
        engraves: ["별빛", "여울", "윤슬"],
        summary: "세릭은 곧게 뻗는 힘과 문양의 정확함이 중심이다. 반짝임과 흐름 계열 각인이 창의 직선성을 돋운다.",
    },
    {
        soulId: "tide-priest-mirea",
        seo: ["수호의 서", "불멸의 서"],
        hon: ["별빛의 혼", "서리의 혼"],
        engraves: ["미리내", "새벽", "윤슬"],
        summary: "미레아는 회복과 정화, 오래 남는 호흡이 강점이다. 미리내와 새벽은 버팀과 회복의 이미지를 자연스럽게 붙여 준다.",
    },
    {
        soulId: "hexgunner-talon",
        seo: ["승부의 서", "섬광의 서"],
        hon: ["어둠의 혼", "화염의 혼"],
        engraves: ["노을", "메아리", "어스름"],
        summary: "탈론은 거칠게 밀고 빠르게 끝내는 쪽이 잘 맞는다. 폭발과 반향, 그림자 계열 각인이 가장 탈론답다.",
    },
    {
        soulId: "frostwarden-borin",
        seo: ["수호의 서", "불멸의 서"],
        hon: ["서리의 혼", "별빛의 혼"],
        engraves: ["새벽", "하늘", "여울"],
        summary: "보린은 느리지만 끝내 무너지지 않는 쪽이 핵심이다. 새벽과 하늘은 담백하지만 오래 가는 수호자 이미지를 만든다.",
    },
];

export function seoPresentation(id: keyof typeof SEO_PRESENTATION): SeoPresentationEntry {
    return SEO_PRESENTATION[id];
}

export function honPresentation(id: keyof typeof HON_PRESENTATION): HonPresentationEntry {
    return HON_PRESENTATION[id];
}

export function engravePresentation(id: keyof typeof ENGRAVE_PRESENTATION): EngravePresentationEntry {
    return ENGRAVE_PRESENTATION[id];
}

export function recommendedLoadoutForSoul(soulId: string): SoulSeoHonLoadout | null {
    return SOUL_RECOMMENDED_LOADOUTS.find((entry) => entry.soulId === soulId) ?? null;
}
